import { useEffect, useMemo, useState } from 'react';
import { isEqual, omit } from 'lodash-es';
import type { FileContainerId, ModelId, ViewId, ViewModelId } from 'balus-common';
import { RTDBPath } from 'balus-common';
import { RecordRepository } from '@framework/repository';
import { FileContainer } from '@view-model/models/sticky/FileContainer/domain/FileContainer';
import type { ViewCollection } from '@view-model/domain/view/ViewCollection';
import type { ViewEntity } from '@view-model/domain/view/ViewEntity';

export type FileContainerListItem = {
  container: FileContainer;
  view: ViewEntity;
};

type Entry = { container: FileContainer; viewId: ViewId };

const toModelIdByViewId = (views: ViewCollection): Record<ViewId, ModelId> =>
  Object.fromEntries(views.map((view) => [view.id, view.modelId]));

export const useFetchFileContainers = (
  viewModelId: ViewModelId,
  views: ViewCollection
): { items: FileContainerListItem[]; totalFileSize: number; firstLoading: boolean } => {
  const [modelIdByViewId, setModelIdByViewId] = useState(() => toModelIdByViewId(views));
  const [entries, setEntries] = useState<Record<FileContainerId, Entry>>({});
  const [firstLoading, setFirstLoading] = useState(true);

  // ビューの位置やタイトルの変更では購読し直さないよう、ビューとモデルの対応が変わったときだけ更新する
  useEffect(() => {
    const next = toModelIdByViewId(views);
    setModelIdByViewId((prev) => (isEqual(prev, next) ? prev : next));
  }, [views]);

  useEffect(() => {
    let cancelled = false;
    const repositories = Object.entries(modelIdByViewId).map(([viewId, modelId]) => {
      const repository = new RecordRepository(
        FileContainer,
        RTDBPath.FileContainer.containersPath(viewModelId, modelId)
      );
      const put = (container: FileContainer) =>
        setEntries((prev) => ({ ...prev, [container.id]: { container, viewId } }));
      repository.addListener(put, put, (container) => setEntries((prev) => omit(prev, container.id)));
      return repository;
    });

    Promise.all(repositories.map((repository) => repository.get()))
      .catch((e) => console.warn(e))
      .finally(() => {
        if (!cancelled) setFirstLoading(false);
      });

    return () => {
      cancelled = true;
      repositories.forEach((repository) => repository.removeListener());
      setEntries({});
    };
  }, [viewModelId, modelIdByViewId]);

  const items = useMemo(
    () =>
      Object.values(entries)
        .flatMap(({ container, viewId }) => {
          const view = views.findById(viewId);
          return view ? [{ container, view }] : [];
        })
        .sort((a, b) => b.container.createdAt.toUnixTimestamp() - a.container.createdAt.toUnixTimestamp()),
    [entries, views]
  );

  const totalFileSize = useMemo(() => items.reduce((sum, { container }) => sum + container.fileSize, 0), [items]);

  return { items, totalFileSize, firstLoading };
};
